/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable no-undef */
// @ts-nocheck
import { sendMessage } from './utils/send-message';
import { parseMessage } from './utils/parse-message';

import { dispatch } from '../store';
import { showAppError, APP_AUTH_ERROR_SAGA } from '../reducers/app';

import { debug } from '../utils/logger';
import { t } from '../locales/i18n';

const maxErrors = 3;
const pingInterval = 30000;
class SocketClient {
  constructor(socketKey, url, deviceUUID, deviceSecret) {
    this.socketKey = socketKey;
    this.url = url;
    this.ws = null;
    this.deviceUUID = deviceUUID;
    this.deviceSecret = deviceSecret;
    this.errorCount = 0;
    this.retryStopped = false;
    this.connectionStartTime = new Date();

    this.pingClearInterval = null;
    this.afterOpenFn = () => {};
    this.onErrorFn = () => {};
  }

  debug(message, data = {}) {
    debug(`ws:${this.socketKey}:${message}  [${this.deviceUUID}]`, data);
  }

  open() {
    this.ws = new WebSocket(this.url);
    this.debug(`trying to open: ${this.url}`);
    return new Promise((resolve) => {
      this.ws.onmessage = (messageEvent) => {
        const data = parseMessage(messageEvent.data);
        if (!data) {
          return;
        }
        const wsAction = {
          ...data,
          authedDeviceUUID: this.deviceUUID,
          type: `web-socket/${data.cmd}/${this.socketKey}`
        };
        if (data.result === 'error') {
          this.debug('receiving error message', data);
          // The server answers `{result:'error', error:{code, message}}`; the
          // flat `error-message` field this used to read never existed on it,
          // so every failure surfaced as an empty toast.
          const errorCode = data.error && data.error.code;
          const errorMessage = (data.error && data.error.message) || data['error-message'];

          // Bad or missing credentials will fail identically on every retry.
          // Reconnecting anyway just spams the server log, so stop and send the
          // operator to /settings.
          if (errorCode === 'err-auth-failed') {
            this.stopConnectRetry();
            dispatch({
              type: APP_AUTH_ERROR_SAGA,
              payload: { deviceUUID: this.deviceUUID }
            });
            return;
          }

          dispatch({
            ...data,
            type: `web-socket/response-error/${this.socketKey}`
          });
          // Translate the code; the server's message is English and only a fallback.
          dispatch(
            showAppError(errorCode ? t(`errors.${errorCode}`, { _: errorMessage || errorCode }) : errorMessage)
          );
          return;
        }
        dispatch(wsAction);
        dispatch({
          type: `web-socket/message-received/${this.socketKey}`,
          ...data
        });
      };

      this.ws.onclose = () => {
        dispatch({ type: `ws/close/${this.socketKey}` });
        this.debug('onclose');
        // The server closes the socket immediately after rejecting auth, so
        // onclose lands right behind stopConnectRetry() and would otherwise
        // schedule the retry we just cancelled.
        if (this.retryStopped) return;
        const retryIntervalInSecs = Math.min(Math.pow(2, this.errorCount), 60) + 1;
        this.intervalId = setTimeout(() => {
          this.debug(`retrying opening connection, attempt ${this.errorCount}`);
          this.open();
        }, retryIntervalInSecs * 1000);
      };

      this.ws.onerror = (err) => {
        this.errorCount += 1;
        dispatch({ type: `ws/error/${this.socketKey}`, err });
        this.debug('onerror', err);
        clearTimeout(this.intervalId);
        if (this.errorCount === maxErrors) {
          this.onErrorFn();
        }
      };

      this.ws.onopen = () => {
        this.debug('onopen');
        clearTimeout(this.intervalId);
        resolve();
        this.afterOpenFn();
        dispatch({ type: `ws/open/${this.socketKey}` });
        this.pingClearInterval = setInterval(() => {
          this.send({
            cmd: 'device/device-status',
            status: {
              userAgent: navigator.userAgent,
              platform: navigator.platform
            }
          });
          this.send({
            cmd: 'booth-queues-status'
          });
        }, pingInterval);
      };
    });
  }

  stopConnectRetry() {
    this.retryStopped = true;
    this.errorCount = maxErrors + 1;
    clearTimeout(this.intervalId);
    clearInterval(this.pingClearInterval);
  }

  send(data) {
    sendMessage(this.ws, data);
  }

  onError(fn) {
    this.onErrorFn = fn;
    clearInterval(this.pingClearInterval);
  }

  afterOpen(fn) {
    this.afterOpenFn = fn;
  }

  close() {
    clearTimeout(this.intervalId);
    this.ws.close();
    clearInterval(this.pingClearInterval);
  }
}

const socketClients = {};

function initClient(type, url, deviceUUID, deviceSecret) {
  if (socketClients[type]) {
    socketClients[type].close();
  }
  socketClients[type] = new SocketClient(type, url, deviceUUID, deviceSecret);
  return socketClients[type];
}

export function initCBXClient(url, deviceUUID, deviceSecret) {
  return initClient('cbx', url, deviceUUID, deviceSecret);
}

export function getCBXSocket() {
  return socketClients.cbx;
}
